import React, { useState, useEffect, useRef } from 'react';
import FormSection from '../FormSection';
import FormField from '../FormField';
import Input from '../Input';
import SearchableSelect from '../SearchableSelect';
import MultiSelectTags from '../MultiSelectTags';
import Textarea from '../Textarea';
import { BasicInstitutionalInfo, SocialNetworks, InstitutionalDescription } from '../../types/profile.types';
import { tiposOrganizacion, countries } from '../../data/profileData';
import './BasicInfoSection.css';
interface BasicInfoSectionProps {
  data: BasicInstitutionalInfo;
  onChange: (data: BasicInstitutionalInfo) => void;
  errors?: Record<string, string>;
  readOnly?: boolean;
  institutionalDescription?: InstitutionalDescription;
  onInstitutionalDescriptionChange?: (data: InstitutionalDescription) => void;
  institutionalDescriptionErrors?: Record<string, string>;
  logoPreview?: string | null;
  /** Cuando true, la sección se usa en el modal "Crear contacto" del CRM; los cambios de texto/requeridos solo aplican aquí y no en el perfil privado */
  createContactMode?: boolean;
}
const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({ 
  data, 
  onChange, 
  errors, 
  readOnly = false,
  createContactMode = false,
  institutionalDescription,
  onInstitutionalDescriptionChange,
  institutionalDescriptionErrors,
  logoPreview,
}) => {
  const logoFileInputRef = React.useRef<HTMLInputElement>(null);
  const [socialNetworks, setSocialNetworks] = useState<SocialNetworks>(data.socialNetworks || {});
  // Sincronizar el estado local con los datos cuando cambian (especialmente importante en modo readOnly)
  useEffect(() => {
    // Actualizar el estado cuando cambian los datos, incluso si es un objeto vacío
    setSocialNetworks(data.socialNetworks || {});
  }, [data.socialNetworks]);
  const [websiteError, setWebsiteError] = useState<string>('');
  const [socialNetworkErrors, setSocialNetworkErrors] = useState<Record<keyof SocialNetworks, string>>({
    facebook: '',
    twitter: '',
    linkedin: '',
    instagram: '',
    youtube: '',
    other: ''
  });


  // Función de validación de URL (igual al form participar)
  const validateUrl = (url: string): boolean => {
    if (url.trim() === '') return true; // Campo opcional
    // Si ya tiene protocolo, validar URL completa
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const urlRegex = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
      return urlRegex.test(url);
    }
    // Si no tiene protocolo, validar solo dominio
    const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
    return domainRegex.test(url);
  };
  const handleChange = (field: keyof BasicInstitutionalInfo, value: any) => {
    onChange({ ...data, [field]: value });
  };
  const handleWebsiteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    handleChange('website', value);
  };

  // Validar website continuamente para que el error persista mientras haya contenido inválido
  useEffect(() => {
    const website = data.website || '';
    if (website.trim() === '') {
      setWebsiteError('');
    } else if (!validateUrl(website)) {
      setWebsiteError('Por favor, ingresa una URL válida');
    } else {
      setWebsiteError('');
    }
  }, [data.website]);
  const handleWebsiteBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    // Si el usuario escribió algo y no tiene protocolo, agregar https://
    if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
      // Verificar si parece un dominio válido
      const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
      if (domainRegex.test(value)) {
        handleChange('website', `https://${value}`);
      }
    }
  };
  const handleSocialNetworkChange = (network: keyof SocialNetworks, value: string) => {
    const updated = { ...socialNetworks, [network]: value };
    setSocialNetworks(updated);
    handleChange('socialNetworks', updated);
  };

  // Validar redes sociales continuamente para que los errores persistan mientras haya contenido inválido
  useEffect(() => {
    const networks = data.socialNetworks || {};
    // CRÍTICO: Crear un nuevo objeto de errores desde cero basado solo en los datos actuales
    // Esto asegura que cuando los campos se vacían externamente, los errores se limpien correctamente
    const newErrors: Record<keyof SocialNetworks, string> = {
      facebook: '',
      twitter: '',
      linkedin: '',
      instagram: '',
      youtube: '',
      other: ''
    };
    
    (Object.keys(networks) as Array<keyof SocialNetworks>).forEach((network) => {
      const value = networks[network] || '';
      if (value.trim() === '') {
        newErrors[network] = '';
      } else if (!validateUrl(value)) {
        newErrors[network] = 'Por favor, ingresa una URL válida';
      } else {
        newErrors[network] = '';
      }
    });
    setSocialNetworkErrors(newErrors);
  }, [data.socialNetworks]);
  const handleSocialNetworkBlur = (network: keyof SocialNetworks, value: string) => {
    const trimmedValue = value.trim();
    // Si el usuario escribió algo y no tiene protocolo, agregar https://
    if (trimmedValue && !trimmedValue.startsWith('http://') && !trimmedValue.startsWith('https://')) {
      // Verificar si parece un dominio válido
      const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
      if (domainRegex.test(trimmedValue)) {
        const updated = { ...socialNetworks, [network]: `https://${trimmedValue}` };
        setSocialNetworks(updated);
        handleChange('socialNetworks', updated);
      }
    }
  };
  return (
    <FormSection
      title={createContactMode ? undefined : 'Información Institucional Básica'}
      description={createContactMode ? undefined : 'Completa los datos básicos de tu organización'}
      required
    >
      {/* Logo de la organización: en createContactMode mismo diseño que perfil privado (Sidebar) */}
      {createContactMode ? (
        <div className="basic-info-logo-section">
          <div className="basic-info-logo-container">
            {data.logo && typeof data.logo === 'string' ? (
              <img
                src={data.logo}
                alt="Logo de la organización"
                onError={() => onChange({ ...data, logo: undefined })}
              />
            ) : (
              <div className="basic-info-logo-placeholder">
                <p>Logo</p>
              </div>
            )}
          </div>
          <input
            ref={logoFileInputRef}
            type="file"
            accept="image/png,image/svg+xml,image/svg,.svg,.ai,.eps,application/postscript,application/illustrator"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => onChange({ ...data, logo: reader.result as string });
              reader.readAsDataURL(file);
              e.target.value = '';
            }}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="basic-info-logo-upload-btn"
            onClick={() => logoFileInputRef.current?.click()}
          >
            {data.logo && typeof data.logo === 'string' ? 'Editar Logo' : 'Subir Logo'}
          </button>
          {data.logo && typeof data.logo === 'string' && (
            <button
              type="button"
              onClick={() => onChange({ ...data, logo: undefined })}
              style={{
                padding: '6px 12px',
                fontSize: '13px',
                border: '1px solid #dc2626',
                color: '#dc2626',
                background: 'transparent',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 600
              }}
            >
              Quitar logo
            </button>
          )}
        </div>
      ) : (
      logoPreview && (
        <div style={{ 
          marginBottom: '32px',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'flex-start'
        }}>
          <div style={{
            width: '200px',
            height: '200px',
            border: '2px solid #e5e7eb',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            backgroundColor: '#ffffff'
          }}>
            <img 
              src={logoPreview} 
              alt="Logo de la organización" 
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
              onError={(e) => {
              }}
            />
          </div>
        </div>
      )
      )}
      {/* Fila 1: Nombre de la organización y Tipo de organización (dos columnas) */}
      <div className="form-row">
        <FormField label="Nombre de la organización" required error={errors?.organizationName}>
          <Input
            value={data.organizationName}
            onChange={(e) => handleChange('organizationName', e.target.value)}
            placeholder="Ingresa el nombre de tu organización"
            error={!!errors?.organizationName}
            disabled={readOnly || !createContactMode}
            title={createContactMode ? undefined : 'Este campo se edita en la pestaña Perfil Personal'}
          />
        </FormField>
        <FormField label="Tipo de organización" required={!createContactMode} error={errors?.organizationType}>
          <SearchableSelect
            value={data.organizationType || ''}
            onChange={(value) => handleChange('organizationType', value)}
            options={tiposOrganizacion}
            placeholder="Seleccionar tipo de organización..."
            allowCustomInput={false}
            error={!!errors?.organizationType}
            disabled={readOnly || !createContactMode}
          />
        </FormField>
      </div>
      {/* Fila 2: Industrias y Sitio web (dos columnas) */}
      <div className="form-row">
        <FormField label="Industrias" required={!createContactMode} error={errors?.industries}>
          <MultiSelectTags
            selectedValues={data.industries || []}
            onChange={(values) => handleChange('industries', values)}
            error={errors?.industries}
            disabled={readOnly || !createContactMode}
          />
        </FormField>
        <FormField label="Sitio web" required={!createContactMode} error={errors?.website || websiteError || undefined}>
          <Input
            type="url"
            value={data.website || ''}
            onChange={handleWebsiteChange}
            onBlur={handleWebsiteBlur}
            placeholder="https://www.ejemplo.com"
            error={!!errors?.website || !!websiteError}
            disabled={readOnly || !createContactMode}
            title={createContactMode ? undefined : 'Este campo se edita en la pestaña Perfil Personal'}
          />
        </FormField>
      </div>
      {/* Fila 3: País (una columna) */}
      <div className="form-row">
        <FormField label="País" required={!createContactMode} error={errors?.country}>
          <SearchableSelect
            value={data.country ? (countries.find(c => c.code === data.country)?.name || '') : ''}
            onChange={(value) => {
              const country = countries.find(c => c.name === value);
              if (country) {
                handleChange('country', country.code);
              }
            }}
            options={countries.map(c => c.name)}
            placeholder="Selecciona un país"
            allowSearch={true}
            error={!!errors?.country}
            disabled={readOnly}
          />
        </FormField>
        <div></div>
      </div>
      {/* Campo de Descripción después del campo País - ocupa las 2 columnas */}
      {institutionalDescription !== undefined && onInstitutionalDescriptionChange && (
        <div className="form-row full-width">
          <FormField 
            label="Descripción" 
            required 
            error={institutionalDescriptionErrors?.description}
          >
            <Textarea
              value={institutionalDescription.description || ''}
              onChange={(e) => onInstitutionalDescriptionChange({ description: e.target.value })}
              placeholder="Describe brevemente tu organización"
              rows={6}
              maxLength={1000}
              showCharCount={true}
              error={!!institutionalDescriptionErrors?.description}
              disabled={readOnly}
            />
          </FormField>
        </div>
      )}
      {/* Leyenda explicativa para campos deshabilitados - oculta en modal crear contacto */}
      {!createContactMode && (
      <div className="info-banner" style={{
        marginTop: '0',
        marginBottom: '8px',
        padding: '12px 16px',
        backgroundColor: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '6px'
      }}>
        <p style={{
          margin: '0',
          fontSize: '12px',
          color: '#64748b',
          fontFamily: "'Montserrat', sans-serif",
          fontStyle: 'italic',
          lineHeight: '1.5'
        }}>
          Los campos "Nombre de la organización", "Tipo de organización", "Sitio web" e "Industrias" se editan en la pestaña <strong className="info-banner-strong" style={{ fontStyle: 'normal', color: '#1e293b' }}>Perfil Personal</strong>. Estos valores se muestran aquí solo como referencia.
        </p>
      </div>
      )}
      <div style={{ marginTop: '24px' }}>
        <h3 className="social-networks-title" style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '18px', fontWeight: 700, color: '#1e293b', marginBottom: '16px' }}>
          Redes Sociales
        </h3>
        <div className="form-row">
          <FormField label="Facebook" error={socialNetworkErrors.facebook}>
            <Input
              type="url"
              value={socialNetworks.facebook || ''}
              onChange={(e) => handleSocialNetworkChange('facebook', e.target.value)}
              onBlur={(e) => handleSocialNetworkBlur('facebook', e.target.value)}
              placeholder="https://www.facebook.com/..."
              error={!!socialNetworkErrors.facebook}
              disabled={readOnly}
            />
          </FormField>
          <FormField label="Twitter/X" error={socialNetworkErrors.twitter}>
            <Input
              type="url"
              value={socialNetworks.twitter || ''}
              onChange={(e) => handleSocialNetworkChange('twitter', e.target.value)}
              onBlur={(e) => handleSocialNetworkBlur('twitter', e.target.value)}
              placeholder="https://twitter.com/..."
              error={!!socialNetworkErrors.twitter}
              disabled={readOnly}
            />
          </FormField>
        </div>
        <div className="form-row">
          <FormField label="LinkedIn" error={socialNetworkErrors.linkedin}>
            <Input
              type="url"
              value={socialNetworks.linkedin || ''}
              onChange={(e) => handleSocialNetworkChange('linkedin', e.target.value)}
              onBlur={(e) => handleSocialNetworkBlur('linkedin', e.target.value)}
              placeholder="https://www.linkedin.com/company/..."
              error={!!socialNetworkErrors.linkedin}
              disabled={readOnly}
            />
          </FormField>
          <FormField label="Instagram" error={socialNetworkErrors.instagram}>
            <Input
              type="url"
              value={socialNetworks.instagram || ''}
              onChange={(e) => handleSocialNetworkChange('instagram', e.target.value)}
              onBlur={(e) => handleSocialNetworkBlur('instagram', e.target.value)}
              placeholder="https://www.instagram.com/..."
              error={!!socialNetworkErrors.instagram}
              disabled={readOnly}
            />
          </FormField>
        </div>
        <div className="form-row">
          <FormField label="YouTube" error={socialNetworkErrors.youtube}>
            <Input
              type="url"
              value={socialNetworks.youtube || ''}
              onChange={(e) => handleSocialNetworkChange('youtube', e.target.value)}
              onBlur={(e) => handleSocialNetworkBlur('youtube', e.target.value)}
              placeholder="https://www.youtube.com/..."
              error={!!socialNetworkErrors.youtube}
              disabled={readOnly}
            />
          </FormField>
          <div></div>
        </div>
      </div>
    </FormSection>
  );
};
export default BasicInfoSection;
